import type { OutreachTemplate, OutreachTemplateGroups, OutreachTemplateScope } from '@discovr/types';

const draftsFirstTouch = (t: OutreachTemplate) =>
  t.kind === 'sequence' && !t.archivedAt && t.steps.some((s) => s.stepNo === 1 && s.subject !== undefined);

/**
 * The template a campaign with no picked sequence drafts from. Mirrors the BE's
 * PickGlobalFirstTouchTemplate: the newest (highest-id) global template that can draft a
 * first touch — never a campaign or brand template that wasn't explicitly picked (owner
 * 2026-10-12). undefined when there is none and the built-in text is used.
 */
export function defaultSequenceTemplate(groups: OutreachTemplateGroups): OutreachTemplate | undefined {
  return groups.global.filter(draftsFirstTouch).sort((a, b) => b.id - a.id)[0];
}

/** The template the campaign drafts from: its pick (archived included), else the global default. */
export function effectiveSequenceTemplate(groups: OutreachTemplateGroups, pickedId: number | null | undefined): OutreachTemplate | undefined {
  if (pickedId == null) return defaultSequenceTemplate(groups);
  return [...groups.campaign, ...groups.brand, ...groups.global].find((t) => t.id === pickedId);
}

export const SCOPE_LABEL: Record<OutreachTemplateScope, string> = { global: 'Global', brand: 'Brand', campaign: 'This campaign' };

export const DEFAULT_TEMPLATE_LABEL = 'Discovr default';

export interface TemplateSection {
  scope: OutreachTemplateScope;
  label: string;
  rows: OutreachTemplate[];
}

/** The start picker's choices, grouped Brand → This campaign; only live sequences. The global library is not listed: the default is offered on its own. */
export function templateSections(groups: OutreachTemplateGroups): TemplateSection[] {
  const pickable = (rows: OutreachTemplate[]) => rows.filter((t) => t.kind === 'sequence' && !t.archivedAt);
  return (['brand', 'campaign'] as const).map((scope) => ({ scope, label: SCOPE_LABEL[scope], rows: pickable(groups[scope]) }));
}

export function templateLabel(template: OutreachTemplate, isDefault: boolean): string {
  if (isDefault) return DEFAULT_TEMPLATE_LABEL;
  return template.scope === 'global' ? `${template.name} (Discovr)` : `${template.name} (${SCOPE_LABEL[template.scope]})`;
}

export const TEMPLATE_LOCKED_COPY = "Locked: this campaign has already sent its first email, so the template can't change."; // copy: ours
export const TEMPLATE_LOAD_ERROR_COPY =
  "Couldn't load the templates, so the template can't be changed now. Drafts use the campaign's current template."; // copy: ours
export const TEMPLATE_SETTINGS_COPY =
  'The template a Bulk send starts from. Each Bulk send can pick another on its Template step.'; // copy: ours

/** The template's name for display: its pick, else the global default, else the built-in text. */
export function chosenTemplateName(groups: OutreachTemplateGroups | undefined, pickedId: number | null | undefined): string {
  if (!groups) return pickedId == null ? DEFAULT_TEMPLATE_LABEL : `Template #${pickedId}`;
  const template = effectiveSequenceTemplate(groups, pickedId);
  if (!template) return pickedId == null ? 'Built-in default message' : `Template #${pickedId}`;
  const label = templateLabel(template, pickedId == null);
  return template.archivedAt ? `${label} · Archived` : label;
}
